import { autocompletion, snippetCompletion, type Completion, type CompletionContext, type CompletionResult } from "@codemirror/autocomplete";
import { StreamLanguage, LanguageSupport, foldService, indentUnit } from "@codemirror/language";
import { stex } from "@codemirror/legacy-modes/mode/stex";
import { EditorSelection, type EditorState, type Extension } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import type { ProjectIndex } from "../lib/api";

const stexLanguage = StreamLanguage.define({
  ...stex,
  languageData: { commentTokens: { line: "%" }, closeBrackets: { brackets: ["(", "[", "{", "$"] } },
});

// ---------------------------------------------------------------- completion data

const SECTIONING = ["part", "chapter", "section", "subsection", "subsubsection", "paragraph", "subparagraph"];

const COMMANDS: Array<[string, string?, string?]> = [
  // [name, snippet after the backslash, detail]
  ...SECTIONING.map((s): [string, string, string] => [s, `${s}{\${title}}`, "sectioning"]),
  ...SECTIONING.slice(0, 5).map((s): [string, string, string] => [`${s}*`, `${s}*{\${title}}`, "unnumbered"]),
  ["textbf", "textbf{${}}", "bold"],
  ["textit", "textit{${}}", "italic"],
  ["emph", "emph{${}}", "emphasis"],
  ["underline", "underline{${}}"],
  ["texttt", "texttt{${}}", "monospace"],
  ["textsc", "textsc{${}}", "small caps"],
  ["textsf", "textsf{${}}"],
  ["textrm", "textrm{${}}"],
  ["textsuperscript", "textsuperscript{${}}"],
  ["textsubscript", "textsubscript{${}}"],
  ["textcolor", "textcolor{${color}}{${text}}"],
  ["footnote", "footnote{${}}"],
  ["label", "label{${}}"],
  ["ref", "ref{${}}"],
  ["eqref", "eqref{${}}"],
  ["autoref", "autoref{${}}"],
  ["cref", "cref{${}}"],
  ["Cref", "Cref{${}}"],
  ["pageref", "pageref{${}}"],
  ["nameref", "nameref{${}}"],
  ["cite", "cite{${}}"],
  ["citep", "citep{${}}"],
  ["citet", "citet{${}}"],
  ["autocite", "autocite{${}}"],
  ["parencite", "parencite{${}}"],
  ["textcite", "textcite{${}}"],
  ["footcite", "footcite{${}}"],
  ["nocite", "nocite{${}}"],
  ["url", "url{${}}"],
  ["href", "href{${url}}{${text}}"],
  ["includegraphics", "includegraphics[width=${0.8}\\linewidth]{${file}}", "image"],
  ["input", "input{${file}}"],
  ["include", "include{${file}}"],
  ["caption", "caption{${}}"],
  ["centering"],
  ["item"],
  ["frac", "frac{${num}}{${den}}"],
  ["dfrac", "dfrac{${num}}{${den}}"],
  ["sqrt", "sqrt{${}}"],
  ["sum", "sum_{${i=1}}^{${n}}"],
  ["prod", "prod_{${i=1}}^{${n}}"],
  ["int", "int_{${a}}^{${b}}"],
  ["lim", "lim_{${x \\to \\infty}}"],
  ["mathrm", "mathrm{${}}"],
  ["mathbf", "mathbf{${}}"],
  ["mathbb", "mathbb{${}}"],
  ["mathcal", "mathcal{${}}"],
  ["mathit", "mathit{${}}"],
  ["boldsymbol", "boldsymbol{${}}"],
  ["text", "text{${}}"],
  ["left", "left${(} ${} \\right${)}"],
  ["right"],
  ["cdot"], ["times"], ["pm"], ["mp"], ["leq"], ["geq"], ["neq"], ["approx"], ["equiv"], ["propto"], ["sim"],
  ["infty"], ["partial"], ["nabla"], ["degree"], ["circ"], ["ldots"], ["cdots"], ["vdots"], ["ddots"],
  ["alpha"], ["beta"], ["gamma"], ["delta"], ["epsilon"], ["varepsilon"], ["zeta"], ["eta"], ["theta"], ["vartheta"],
  ["iota"], ["kappa"], ["lambda"], ["mu"], ["nu"], ["xi"], ["pi"], ["rho"], ["sigma"], ["tau"], ["upsilon"], ["phi"],
  ["varphi"], ["chi"], ["psi"], ["omega"], ["Gamma"], ["Delta"], ["Theta"], ["Lambda"], ["Xi"], ["Pi"], ["Sigma"],
  ["Phi"], ["Psi"], ["Omega"],
  ["rightarrow"], ["leftarrow"], ["Rightarrow"], ["Leftarrow"], ["leftrightarrow"], ["to"], ["mapsto"],
  ["hat", "hat{${}}"], ["bar", "bar{${}}"], ["vec", "vec{${}}"], ["dot", "dot{${}}"], ["ddot", "ddot{${}}"], ["tilde", "tilde{${}}"],
  ["overline", "overline{${}}"], ["underbrace", "underbrace{${}}_{${}}"],
  ["SI", "SI{${value}}{${unit}}", "siunitx"], ["si", "si{${unit}}", "siunitx"], ["qty", "qty{${value}}{${unit}}", "siunitx"], ["unit", "unit{${}}", "siunitx"], ["num", "num{${}}", "siunitx"],
  ["newcommand", "newcommand{\\${name}}[${args}]{${definition}}"],
  ["renewcommand", "renewcommand{\\${name}}{${definition}}"],
  ["usepackage", "usepackage{${}}"],
  ["documentclass", "documentclass{${article}}"],
  ["title", "title{${}}"], ["author", "author{${}}"], ["date", "date{${\\today}}"], ["today"],
  ["maketitle"], ["tableofcontents"], ["listoffigures"], ["listoftables"], ["appendix"],
  ["newpage"], ["clearpage"], ["cleardoublepage"], ["pagebreak"], ["linebreak"], ["noindent"], ["par"],
  ["vspace", "vspace{${1em}}"], ["hspace", "hspace{${1em}}"], ["hfill"], ["vfill"], ["smallskip"], ["medskip"], ["bigskip"],
  ["bibliography", "bibliography{${refs}}"], ["bibliographystyle", "bibliographystyle{${plain}}"],
  ["printbibliography"], ["addbibresource", "addbibresource{${refs.bib}}"],
  ["hline"], ["toprule"], ["midrule"], ["bottomrule"], ["cline", "cline{${1-2}}"], ["multicolumn", "multicolumn{${2}}{${c}}{${}}"], ["multirow", "multirow{${2}}{*}{${}}"],
  ["linewidth"], ["textwidth"], ["columnwidth"],
  ["small"], ["footnotesize"], ["scriptsize"], ["tiny"], ["large"], ["Large"], ["LARGE"], ["huge"], ["Huge"], ["normalsize"],
  ["gls", "gls{${}}", "glossaries"], ["Gls", "Gls{${}}", "glossaries"], ["glspl", "glspl{${}}", "glossaries"], ["acrshort", "acrshort{${}}"], ["acrlong", "acrlong{${}}"], ["acrfull", "acrfull{${}}"],
  ["subfloat", "subfloat[${caption}]{${}}"], ["subcaption", "subcaption{${}}"],
  ["todo", "todo{${}}", "todonotes"],
];

const ENVIRONMENTS: Array<[string, string?]> = [
  ["figure", "[htbp]\n\t\\centering\n\t\\includegraphics[width=0.8\\linewidth]{${file}}\n\t\\caption{${caption}}\n\t\\label{fig:${label}}\n"],
  ["table", "[htbp]\n\t\\centering\n\t\\caption{${caption}}\n\t\\label{tab:${label}}\n\t\\begin{tabular}{${ll}}\n\t\t${}\n\t\\end{tabular}\n"],
  ["tabular", "{${ll}}\n\t${}\n"],
  ["itemize", "\n\t\\item ${}\n"],
  ["enumerate", "\n\t\\item ${}\n"],
  ["description", "\n\t\\item[${term}] ${}\n"],
  ["equation", "\n\t${}\n"],
  ["equation*", "\n\t${}\n"],
  ["align", "\n\t${}\n"],
  ["align*", "\n\t${}\n"],
  ["gather", "\n\t${}\n"],
  ["multline", "\n\t${}\n"],
  ["split", "\n\t${}\n"],
  ["cases", "\n\t${}\n"],
  ["matrix", "\n\t${}\n"],
  ["pmatrix", "\n\t${}\n"],
  ["bmatrix", "\n\t${}\n"],
  ["subfigure", "{${0.48}\\linewidth}\n\t\\centering\n\t${}\n"],
  ["minipage", "{${0.48}\\linewidth}\n\t${}\n"],
  ["center", "\n\t${}\n"],
  ["flushleft", "\n\t${}\n"],
  ["flushright", "\n\t${}\n"],
  ["quote", "\n\t${}\n"],
  ["quotation", "\n\t${}\n"],
  ["verbatim", "\n${}\n"],
  ["lstlisting", "\n${}\n"],
  ["minted", "{${python}}\n${}\n"],
  ["abstract", "\n\t${}\n"],
  ["document", "\n${}\n"],
  ["theorem", "\n\t${}\n"],
  ["lemma", "\n\t${}\n"],
  ["proof", "\n\t${}\n"],
  ["definition", "\n\t${}\n"],
  ["tikzpicture", "\n\t${}\n"],
  ["axis", "[${}]\n\t${}\n"],
  ["frame", "{${title}}\n\t${}\n"],
  ["columns", "\n\t\\column{0.5\\textwidth}\n\t${}\n"],
  ["appendices", "\n${}\n"],
  ["longtable", "{${ll}}\n\t${}\n"],
  ["landscape", "\n${}\n"],
];

const PACKAGES = [
  "amsmath", "amssymb", "amsthm", "mathtools", "graphicx", "xcolor", "hyperref", "cleveref", "geometry", "fancyhdr",
  "babel", "fontenc", "inputenc", "lmodern", "microtype", "booktabs", "tabularx", "longtable", "multirow", "array",
  "caption", "subcaption", "float", "wrapfig", "siunitx", "biblatex", "natbib", "csquotes", "enumitem", "listings",
  "minted", "tikz", "pgfplots", "pdfpages", "setspace", "titlesec", "tocloft", "appendix", "glossaries", "acronym",
  "nomencl", "todonotes", "lipsum", "url", "fontspec", "unicode-math", "physics", "bm", "parskip", "etoolbox",
  "xparse", "makecell", "threeparttable", "rotating", "pdflscape", "chemfig", "mhchem", "algorithm2e", "algorithmicx",
  "algpseudocode", "datetime2", "lastpage", "afterpage", "placeins", "svg", "standalone", "subfiles", "import",
];

const REF_CMDS = /\\(?:[a-zA-Z]*ref|[cC]ref|[cC]pageref|vref|Vref|autoref|nameref|labelcref)\*?\{([^}]*)$/;
const CITE_CMDS = /\\(?:[a-zA-Z]*cite[a-zA-Z]*|nocite)\*?(?:\[[^\]]*\]){0,2}\{([^}]*)$/;
const FILE_CMDS = /\\(input|include|includegraphics|includepdf|includeonly|subfile|addbibresource|bibliography|import|lstinputlisting|inputminted|includesvg)\*?(?:\[[^\]]*\])?(?:\{[^}]*\})?\{([^}]*)$/;
const BEGIN_CMD = /\\(begin|end)\{([^}]*)$/;
const PKG_CMD = /\\(?:usepackage|RequirePackage)(?:\[[^\]]*\])?\{([^}]*)$/;

function fileCompletions(index: ProjectIndex, cmd: string): Completion[] {
  const exts: Record<string, RegExp> = {
    includegraphics: /\.(png|jpe?g|pdf|eps|svg)$/i,
    includesvg: /\.svg$/i,
    includepdf: /\.pdf$/i,
    addbibresource: /\.bib$/i,
    bibliography: /\.bib$/i,
    input: /\.(tex|tikz)$/i,
    include: /\.tex$/i,
    subfile: /\.tex$/i,
  };
  const re = exts[cmd] ?? /./;
  const stripExt = ["input", "include", "subfile", "bibliography"].includes(cmd);
  return index.files
    .filter((f) => re.test(f))
    .map((f) => ({ label: stripExt ? f.replace(/\.(tex|bib)$/i, "") : f, type: "file", detail: stripExt ? f : undefined }));
}

export function latexCompletions(getIndex: () => ProjectIndex | null) {
  return (ctx: CompletionContext): CompletionResult | null => {
    const line = ctx.state.doc.lineAt(ctx.pos);
    const before = line.text.slice(0, ctx.pos - line.from);
    const index = getIndex();
    let m: RegExpExecArray | null;

    if ((m = BEGIN_CMD.exec(before))) {
      const isBegin = m[1] === "begin";
      const typed = m[2];
      const from = ctx.pos - typed.length;
      const after = ctx.state.sliceDoc(ctx.pos, Math.min(line.to, ctx.pos + 1));
      return {
        from,
        validFor: /^[\w*]*$/,
        options: ENVIRONMENTS.map(([env, body]) =>
          isBegin && after !== "}"
            ? snippetCompletion(`${env}}${body ?? "\n\t${}\n"}\\end{${env}}`, { label: env, type: "type", detail: "environment" })
            : { label: env, type: "type", apply: after === "}" ? env : `${env}}` },
        ),
      };
    }
    if ((m = REF_CMDS.exec(before)) && index) {
      const typed = m[1].split(",").pop() ?? "";
      return {
        from: ctx.pos - typed.length,
        validFor: /^[^,}]*$/,
        options: index.labels.map((l) => ({ label: l.label, type: "variable", detail: `${l.file}:${l.line}` })),
      };
    }
    if ((m = CITE_CMDS.exec(before)) && index) {
      const typed = (m[1].split(",").pop() ?? "").trimStart();
      return {
        from: ctx.pos - typed.length,
        validFor: /^[^,}\s]*$/,
        options: index.citations.map((c) => ({
          label: c.key,
          type: "constant",
          detail: [c.author?.split(/\s+and\s+/)[0]?.split(",")[0], c.year].filter(Boolean).join(" "),
          info: c.title,
        })),
      };
    }
    if ((m = FILE_CMDS.exec(before)) && index) {
      const typed = m[2];
      return { from: ctx.pos - typed.length, validFor: /^[^}]*$/, options: fileCompletions(index, m[1]) };
    }
    if ((m = PKG_CMD.exec(before))) {
      const typed = m[1].split(",").pop() ?? "";
      return { from: ctx.pos - typed.length, validFor: /^[\w-]*$/, options: PACKAGES.map((p) => ({ label: p, type: "namespace" })) };
    }
    const word = ctx.matchBefore(/\\[a-zA-Z@]*\*?/);
    if (word && (word.from !== word.to || ctx.explicit)) {
      const custom = (index?.commands ?? []).map((c) => ({ label: `\\${c}`, type: "function", detail: "project" }));
      const builtins = COMMANDS.map(([name, snippet, detail]) =>
        snippet ? snippetCompletion(`\\${snippet}`, { label: `\\${name}`, type: "keyword", detail }) : { label: `\\${name}`, type: "keyword", detail },
      );
      return { from: word.from, validFor: /^\\[a-zA-Z@]*\*?$/, options: [...builtins, ...custom] };
    }
    return null;
  };
}

// ---------------------------------------------------------------- commands

function wrapWith(cmd: string) {
  return (view: EditorView) => {
    if (view.state.readOnly) return false;
    view.dispatch(
      view.state.changeByRange((range) => {
        const text = view.state.sliceDoc(range.from, range.to);
        const insert = `\\${cmd}{${text}}`;
        return {
          changes: { from: range.from, to: range.to, insert },
          range: range.empty
            ? EditorSelection.cursor(range.from + cmd.length + 2)
            : EditorSelection.range(range.from + cmd.length + 2, range.from + cmd.length + 2 + text.length),
        };
      }),
    );
    return true;
  };
}

/** Fold \begin{x} ... \end{x} and sections. */
const envFold = foldService.of((state: EditorState, lineStart: number) => {
  const line = state.doc.lineAt(lineStart);
  const begin = /\\begin\{([^}]+)\}/.exec(line.text);
  if (begin) {
    const name = begin[1];
    let depth = 0;
    for (let n = line.number; n <= Math.min(state.doc.lines, line.number + 2000); n++) {
      const text = state.doc.line(n).text;
      for (const m of text.matchAll(/\\(begin|end)\{([^}]+)\}/g)) {
        if (m[2] !== name) continue;
        depth += m[1] === "begin" ? 1 : -1;
        if (depth === 0 && n > line.number) return { from: line.to, to: state.doc.line(n).from + (m.index ?? 0) };
      }
    }
  }
  const sec = /^\s*\\(part|chapter|section|subsection|subsubsection)\*?[[{]/.exec(line.text);
  if (sec) {
    const level = SECTIONING.indexOf(sec[1]);
    for (let n = line.number + 1; n <= state.doc.lines; n++) {
      const t = state.doc.line(n).text;
      const m = /^\s*\\(part|chapter|section|subsection|subsubsection)\*?[[{]/.exec(t);
      if ((m && SECTIONING.indexOf(m[1]) <= level) || /^\s*\\end\{document\}/.test(t)) {
        const end = state.doc.line(n - 1).to;
        return end > line.to ? { from: line.to, to: end } : null;
      }
    }
  }
  return null;
});

export function latex(getIndex: () => ProjectIndex | null, onCompile: () => void): Extension {
  return [
    new LanguageSupport(stexLanguage),
    indentUnit.of("  "),
    envFold,
    autocompletion({ override: [latexCompletions(getIndex)], icons: false, activateOnTyping: true, maxRenderedOptions: 80 }),
    keymap.of([
      { key: "Mod-b", run: wrapWith("textbf"), preventDefault: true },
      { key: "Mod-i", run: wrapWith("textit"), preventDefault: true },
    ]),
    compileKeys(onCompile),
  ];
}

/** Ctrl/⌘+S and Ctrl/⌘+Enter compile from any file, LaTeX or not. */
export function compileKeys(onCompile: () => void): Extension {
  const run = () => {
    onCompile();
    return true;
  };
  return keymap.of([
    { key: "Mod-s", run, preventDefault: true },
    { key: "Mod-Enter", run, preventDefault: true },
  ]);
}

// ---------------------------------------------------------------- outline

export interface OutlineEntry {
  level: number;
  title: string;
  line: number;
  kind: string;
}

export function parseOutline(text: string): OutlineEntry[] {
  const out: OutlineEntry[] = [];
  const lines = text.split("\n");
  const re = /\\(part|chapter|section|subsection|subsubsection|paragraph)(\*)?\s*(?:\[[^\]]*\])?\s*\{/;
  for (let i = 0; i < lines.length; i++) {
    const code = lines[i].replace(/(^|[^\\])%.*$/, "$1");
    const m = re.exec(code);
    if (!m) continue;
    // Read a balanced {...} title, possibly spanning a couple of lines.
    let rest = code.slice((m.index ?? 0) + m[0].length);
    let j = i;
    let depth = 1;
    let title = "";
    outer: while (true) {
      for (const ch of rest) {
        if (ch === "{") depth++;
        else if (ch === "}" && --depth === 0) break outer;
        title += ch;
      }
      if (++j >= lines.length || j > i + 3) break;
      rest = lines[j];
      title += " ";
    }
    out.push({ level: SECTIONING.indexOf(m[1]), title: cleanTitle(title), line: i + 1, kind: m[1] + (m[2] ?? "") });
  }
  return out;
}

function cleanTitle(t: string): string {
  return t
    .replace(/\\(?:texorpdfstring)\{([^}]*)\}\{[^}]*\}/g, "$1")
    .replace(/\\[a-zA-Z]+\*?(?:\[[^\]]*\])?\{([^}]*)\}/g, "$1")
    .replace(/\\[a-zA-Z]+\*?/g, "")
    .replace(/[{}~]/g, " ")
    .replace(/\$([^$]*)\$/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export const editorTheme = EditorView.theme({
  "&": { height: "100%" },
  ".cm-content": { padding: "10px 0 40vh" },
  ".cm-line": { padding: "0 14px 0 8px" },
  ".cm-gutterElement": { padding: "0 6px 0 10px" },
});
